import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dyndev-green focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'bg-dyndev-green text-dyndev-dark hover:bg-opacity-90 shadow-sm hover:shadow-md hover:scale-[1.02]',
        destructive:
          'bg-red-500 text-white hover:bg-red-600 shadow-sm',
        outline:
          'border border-[var(--border-color)] bg-transparent hover:bg-[var(--bg-secondary)] hover:text-[var(--text-primary)]',
        secondary:
          'bg-[var(--bg-secondary)] text-[var(--text-primary)] hover:bg-[var(--bg-primary)] border border-[var(--border-color)]',
        ghost:
          'hover:bg-[var(--bg-secondary)] hover:text-[var(--text-primary)]',
        link:
          'text-dyndev-green underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 rounded-md px-3 text-xs',
        lg: 'h-11 rounded-lg px-8 text-base',
        icon: 'h-10 w-10',
        pill: 'h-10 px-5 rounded-full',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        type={asChild ? undefined : 'button'}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
